import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
  Injectable,
  Inject,
  LOCALE_ID,
  DestroyRef,
} from '@angular/core';

import { ReactiveFormsModule, FormsModule, FormControl } from '@angular/forms';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  DateAdapter,
  NativeDateAdapter,
  MAT_DATE_FORMATS,
  MatDateFormats,
} from '@angular/material/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InputWrapperComponent } from '../components/input-wrapper/input-wrapper.component';

export const CUSTOM_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: ['DD/MM/YYYY', 'MM/YYYY'],
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Injectable()
export class DynamicModeDateAdapter extends NativeDateAdapter {
  private _mode: 'full' | 'monthYear' = 'full';

  setMode(mode: 'full' | 'monthYear') {
    this._mode = mode;
  }

  override format(date: Date, displayFormat: Object): string {
    if (this._mode === 'monthYear') {
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${this._to2digit(month)}/${year}`;
    }
    return super.format(date, displayFormat);
  }

  private _to2digit(n: number) {
    return ('00' + n).slice(-2);
  }
}

@Component({
  selector: 'app-date-range',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    InputWrapperComponent,
    FormsModule
],
  providers: [
    { provide: DateAdapter, useClass: DynamicModeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
  ],
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeComponent implements OnChanges {
  @Input() id?: string;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() mode: 'full' | 'monthYear' = 'full';
  @Input() disabled: boolean = false;
  @Input() titleLabel?: string;
  @Input() infoLabel?: string;
  @Input() startDate?: Date | null;
  @Input() endDate?: Date | null;
  @Input() startAt?: Date;
  @Input() endAt?: Date;

  @Output() startDateChange = new EventEmitter<Date | null>();
  @Output() endDateChange = new EventEmitter<Date | null>();

  startControl = new FormControl<Date | null>(null);
  endControl = new FormControl<Date | null>(null);

  computedMinStart: Date | null = null;
  computedMaxStart: Date | null = null;
  computedMinEnd: Date | null = null;
  computedMaxEnd: Date | null = null;

  constructor(
    private readonly dateAdapter: DateAdapter<Date>,
    private readonly destroyRef: DestroyRef,
    @Inject(LOCALE_ID) private readonly locale: string,
  ) {
    this.dateAdapter.setLocale(this.locale);

    this.startControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      let newStart = val;
      if (this.mode === 'monthYear' && val instanceof Date) {
        const firstDay = new Date(val.getFullYear(), val.getMonth(), 1);

        if (this.minDate && firstDay < this.minDate) {
          newStart = this.minDate;
        } else {
          newStart = firstDay;
        }
      }
      this.startDate = newStart;
      this.updateConstraints();
      this.startDateChange.emit(newStart);
    });

    this.endControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      let newEnd = val;
      if (this.mode === 'monthYear' && val instanceof Date) {
        newEnd = new Date(val.getFullYear(), val.getMonth() + 1, 0, 23, 59, 59, 999);
      } else if (val instanceof Date) {
        newEnd = new Date(val.getFullYear(), val.getMonth(), val.getDate(), 23, 59, 59, 999);
      }
      this.endDate = newEnd;
      this.updateConstraints();
      this.endDateChange.emit(newEnd);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mode']) {
      const adapter = this.dateAdapter as DynamicModeDateAdapter;
      adapter.setMode(this.mode);

      if (this.startControl.value) this.startControl.setValue(new Date(this.startControl.value));
      if (this.endControl.value) this.endControl.setValue(new Date(this.endControl.value));
    }

    if (changes['minDate'] || changes['maxDate'] || changes['startDate'] || changes['endDate']) {
      if (changes['startDate'] && changes['startDate'].currentValue !== this.startControl.value) {
        this.startControl.setValue(this.startDate || null, {
          emitEvent: false,
        });
      }
      if (changes['endDate'] && changes['endDate'].currentValue !== this.endControl.value) {
        this.endControl.setValue(this.endDate || null, { emitEvent: false });
      }
      this.updateConstraints();
    }

    if (changes['disabled']) {
      this.toggleDatePickersState();
    }
  }

  toggleDatePickersState() {
    if (this.disabled) {
      this.startControl.disable();
      this.endControl.disable();
    } else {
      this.startControl.enable();
      this.endControl.enable();
    }
  }

  private updateConstraints(): void {
    this.computedMinStart = this.minDate || null;
    this.computedMaxStart = this.endDate ? this.endDate : this.maxDate || null;

    this.computedMinEnd = this.startDate ? this.startDate : this.minDate || null;
    this.computedMaxEnd = this.maxDate || null;
  }

  monthSelected(event: Date, picker: MatDatepicker<Date>, control: FormControl) {
    if (this.mode === 'monthYear') {
      const day = this.minDate?.getDate() ?? 1;
      let normalizedDate = new Date(event.getFullYear(), event.getMonth(), day);
      if (this.minDate && normalizedDate < this.minDate) {
        normalizedDate = new Date(this.minDate);
      }
      if (this.maxDate && normalizedDate > this.maxDate) {
        normalizedDate = new Date(this.maxDate);
      }
      control.setValue(normalizedDate);
      picker.close();
    }
  }
}
